import {Component, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';

import {TranslateService} from '@ngx-translate/core';

import {BehaviorSubject, combineLatest, EMPTY, from, Observable} from 'rxjs';
import {debounceTime, delay, distinctUntilChanged, filter, finalize, switchMap, takeUntil, tap} from 'rxjs/operators';

import * as moment from 'moment';

import {ToastrService} from 'ngx-toastr';
import {Link} from '../../+employees/emp-tree/emp-tree.interfaces';
import {EmployeeBackup, ESSService} from '../../+ess/employee-self.service';
import {DATE_FORMAT, DATE_MAX} from '../../app.constants';
import {Destroyable} from '../../destroyable.component';
import {ApiService} from '../../shared/api';
import {Contract, Employee} from '../../shared/employees/employees.model';
import {ModalService} from '../../shared/modal';
import {DateLocalizationPipe} from '../../shared/pipes/date-localization.pipe';
import {TeamsService} from '../../shared/teams/teams.service';
import {getInitials} from '../../shared/utils/strings';

const onlyActiveContracts = () => contract => !moment(contract.enddate).isBefore(moment(new Date()).format(DATE_FORMAT));

@Component({
  selector: 'gp-backups',
  templateUrl: './backups.component.html',
  styleUrls: ['./backups.component.scss']
})
export class BackupsComponent extends Destroyable implements OnInit {

  public static ACCESS_NAME = 'MSSBackups';
  public loading = false;
  public loadingContracts = false;
  public backups: EmployeeBackup[];
  public backupForm: UntypedFormGroup;
  public contracts: Contract[];
  public employees: {value: string, display: string}[];
  public displayContracts: {value: number, display: string}[];
  public searchTerm: BehaviorSubject<string> = new BehaviorSubject('');

  constructor(public essService: ESSService,
              public teamService: TeamsService,
              public api: ApiService,
              private dateLocalizationPipe: DateLocalizationPipe,
              private formBuilder: UntypedFormBuilder,
              private modalService: ModalService,
              private toastrService: ToastrService,
              private translateService: TranslateService) {
    super();
  }

  ngOnInit(): void {
    this.backupForm = this.formBuilder.group( {
      contractId: ['', Validators.required],
      contractBeginDate: [''],
      contractEndDate: [''],
      backupEmployeeId: ['', Validators.required],
      backupBeginDate: ['', Validators.required],
      backupEndDate: ['', Validators.required],
    },
      {validators: [this.datesValidator]});


    combineLatest(
      [
        this.backupForm.get('backupBeginDate').valueChanges,
        this.backupForm.get('backupEndDate').valueChanges,
        this.searchTerm.asObservable().pipe(debounceTime(350), distinctUntilChanged())])
      .pipe(
        switchMap(([backupBeginDate, backupEndDate, searchTerm]) =>
          this.teamService.getAllEmployees(backupBeginDate, backupEndDate, searchTerm)),
        tap( employees => {
          this.employees = employees.map(employee =>
            ({value: employee.id, display: this.setEmployeeDisplay(employee.name, employee.employeeNumber, employee.photoLink)}));
          if ( this.backupForm.get('backupEmployeeId').value && !this.employees.find(employee => employee.value === this.backupForm.get('backupEmployeeId').value)) {
            this.resetSelectedBackup();
          }
        }),
        takeUntil(this.componentDestroyed$)
    ).subscribe();

    this.backupForm.get('contractId').valueChanges.pipe(
      tap((value) => {
        if (value) {
          this.backupForm.get('contractBeginDate').setValue(this.getSelectedContract(value).begindate);
          this.backupForm.get('contractEndDate').setValue(this.getSelectedContract(value).enddate);
        }
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();

    this.initBackupDates();
    this.loadingContracts = true;
    this.essService.currentEmployee
      .pipe(
        filter(Boolean),
        tap((employee: Employee) => {
          this.contracts = employee?.contracts.filter(onlyActiveContracts());
          if (this.contracts?.length) {
            this.backupForm.get('contractId').setValue(this.contracts[0]?.id);
            this.displayContracts = this.contracts.map(contract => ({
              value: contract.id,
              display: this.setContractDisplay(contract)
            }));
          }
        }),
        switchMap(() => this.getBackups()),
        finalize(() => this.loadingContracts = false),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

  }

  public setContractDisplay(contract: Contract) {
    const text: string = this.translateService.instant('EMPLOYEE_FORM.Contract_name', {
      name: contract.cliRegNickName
    });
      return contract.flag
        ? `<img class="flag-icon" src="${contract.flag.href}" />
            ${text} ${this.getContractDate(contract)}`
        : `${text} ${this.getContractDate(contract)}`;
  }

  public setEmployeeDisplay(name: string, employeeId: string, avatarUrl?: Link): string {
    const hasPhoto = avatarUrl?.href && !avatarUrl.href.includes('null') && !avatarUrl.href.includes('undefined');
    const parts = name.trim().split(/\s+/);
    const initials = getInitials(parts[0], parts.length > 1 ? parts[parts.length - 1] : '');
    const avatar = hasPhoto
      ? `<span class="circle avatar avatar-text w-32">
            <img src="${this.api.apiHost + avatarUrl.href}" alt="">
        </span>`
      : `<span class="gp-initials-avatar gp-avatar-md">${initials}</span>`;

    return `<div class="emp">
              <div class="emp-avatar">
                    ${avatar}
              </div>
              <div class="emp-info">
                <div>${name}</div>
                <small class="text-muted">${employeeId}</small>
            </div>
          </div>`;
  }

  public getContractDate(contract: Contract): string {
    return `(${
      this.dateLocalizationPipe.transform(contract.begindate)
    } - ${
      contract.enddate === DATE_MAX || !contract.enddate
        ? '...'
        : this.dateLocalizationPipe.transform(contract.enddate)
    })`;
  }

  private getBackups(): Observable<EmployeeBackup[]> {
    this.loading = true;
    return this.teamService.getBackups().pipe(
      tap(response => this.backups = response),
      finalize(() => this.loading = false)
    );
  }

  public addBackup() {
    this.teamService.addBackup(this.backupForm.value).pipe(
      switchMap(() => this.getBackups()),
      tap(() => this.resetSelectedBackup())
    ).subscribe();
  }

  public filterEmployee(event) {
    this.searchTerm.next(event);
  }


  public deleteBackup(backup: EmployeeBackup) {
    from(this.modalService.confirm({
      title: 'DIALOG.delete_backup',
      text: 'DIALOG.confirm_delete_backup?',
      size: 'sm',
      type: 'danger'
    })).pipe(
      delay(500),
      switchMap(confirm => {
        return confirm
          ? this.teamService.deleteBackup(backup.backupId)
            .pipe(
              switchMap(() => {
                return this.getBackups();
              }))
          : EMPTY;
      }),
      switchMap(() => this.translateService.get('MSS.backup_has_been_deleted').pipe(
        tap((message: string) => this.toastrService.success(message, '')))
      ),
      takeUntil(this.componentDestroyed$))
      .subscribe();
  }

  private initBackupDates() {
    this.backupForm.get('backupBeginDate').setValue(moment(new Date()).format(DATE_FORMAT));
    this.backupForm.get('backupEndDate').setValue(moment(new Date()).format(DATE_FORMAT));
  }

  public resetSelectedBackup() {
    this.backupForm.patchValue({
      backupEmployeeId: '',
    });

    this.backupForm.markAsPristine();
  }

  public clearBackupForm() {
    this.backupForm.patchValue({
      backupEmployeeId: '',
      backupBeginDate: moment(new Date()).format(DATE_FORMAT),
      backupEndDate: moment(new Date()).format(DATE_FORMAT),
    });
    this.backupForm.markAsPristine();
    this.backupForm.updateValueAndValidity();
  }

  private getSelectedContract(contractId: number): Contract {
    return this.contracts.find(contract => contract.id === contractId);
  }

  private datesValidator(form: UntypedFormGroup) {
      const beginDateControl = form.get('backupBeginDate');
      const endDateControl = form.get('backupEndDate');
      const contractBeginDateControl = form.get('contractBeginDate');
      const contractEndDateControl = form.get('contractEndDate');
      if (!endDateControl || !beginDateControl) {
        return null;
      }
      const beginDateAfterEndDate = moment(beginDateControl.value).isAfter(moment(endDateControl.value));
      const beginDateBeforeContractStart =
        form.get('contractId').value && moment(beginDateControl.value).isBefore(moment(contractBeginDateControl.value));
      const beginDateAfterEndOfContract =
        form.get('contractId').value && moment(beginDateControl.value).isAfter(moment(contractEndDateControl.value));
      const endDateAfterContractEnd =
        form.get('contractId').value && moment(endDateControl.value).isAfter(moment(contractEndDateControl.value));
      const endDateBeforeToday =
        moment(moment(new Date(Date.now())).format(DATE_FORMAT)).isAfter(moment(endDateControl.value));
      const endDateBeforeContractStart =
        form.get('contractId').value && moment(endDateControl.value).isBefore(moment(contractBeginDateControl.value));


      return !beginDateAfterEndDate && !beginDateBeforeContractStart && !beginDateAfterEndOfContract && !endDateAfterContractEnd && !endDateBeforeToday && !endDateBeforeContractStart
      ? null : {beginDateAfterEndDate, beginDateBeforeContractStart, beginDateAfterEndOfContract, endDateAfterContractEnd, endDateBeforeToday, endDateBeforeContractStart};
    }



}
